import { Container, Graphics, MeshRope, Point, Sprite } from 'pixi.js';
import { GlowFilter } from '@pixi/filter-glow';
import { G, keys, isTouchDevice } from './state';
import { getActiveTrail, getTrailTextures } from './trails';
import { SPRITES } from './sprites';

const MOUSE_LAG_FRAMES = 6;
const VEL_LERP = 0.08 * 60;
const TARGET_PULL = 3.0;
const MAX_SPEED = 700;
const TRAIL_MAX = 60;
const KEYS_ACCEL = 1800;

export interface StarshipConfig {
  tint?: number;
  useKeys?: boolean;
  startX?: number;
  startY?: number;
}

export class Player {
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  hp = 60;
  maxHp = 60;
  shield = 30;
  maxShield = 30;
  invuln = 0;
  radius = 18;
  aimAng = 0;
  trail: Array<{ x: number; y: number }> = [];
  // Post-warp momentum — when > 0, mouse-pull is suppressed so the random warp
  // exit-angle survives instead of getting lerped back toward the cursor immediately.
  warpMomentumT = 0;
  // Respawn arrival animation — when > 0, ship slides in from the left ignoring mouse
  arrivalT = 0;
  private arrivalDur = 0;
  private arrivalEndX = 0;
  private arrivalEndY = 0;
  private arrivalStartX = -60;

  dead = false;
  firing = false;
  fireTimer = 0;
  view: Container;
  private body: Sprite;
  private ropeOuter!: MeshRope;
  private ropeMid!: MeshRope;
  private ropeCore!: MeshRope;
  private ropePoints: Point[] = [];
  private currentSkinId = '';
  private flameGfx: Graphics;
  private engineSpr!: Sprite;
  private cockpitSpr!: Sprite;
  private cannonL!: Sprite;
  private cannonR!: Sprite;
  private flameT = 0;
  private glowT = 0;
  private baseGlowStrength = 1.8;
  private glow: GlowFilter | null = null;
  private trailGfxOverlay!: Graphics;
  private cfg: StarshipConfig = {};

  constructor(stage: Container, startX?: number, startY?: number, cfg: StarshipConfig = {}) {
    this.cfg = cfg;
    this.x = startX ?? G.W * 0.25;
    this.y = startY ?? G.H / 2;
    // Pre-allocate fixed-length point buffer so MeshRope geometry stays stable.
    for (let i = 0; i < TRAIL_MAX; i++) this.ropePoints.push(new Point(this.x, this.y));
    this.buildRopes(stage);
    this.trailGfxOverlay = new Graphics();
    this.trailGfxOverlay.blendMode = 'add';
    stage.addChild(this.trailGfxOverlay);
    this.view = new Container();
    // Engine glow halo — sits behind flame, pulses on boost
    this.engineSpr = new Sprite(SPRITES.engineGlow);
    this.engineSpr.anchor.set(0.5, 0.5);
    this.engineSpr.position.set(-30, 0);
    this.engineSpr.tint = cfg.tint ?? 0xff8800;
    this.engineSpr.blendMode = 'add';
    this.engineSpr.alpha = 0.55;
    this.view.addChild(this.engineSpr);
    this.flameGfx = new Graphics();
    this.flameGfx.blendMode = 'add';
    this.view.addChild(this.flameGfx);
    this.body = new Sprite(SPRITES.playerP1Tiers[cfg.useKeys ? 1 : 0]);
    this.body.anchor.set(0.5, 0.5);
    if (cfg.tint) this.body.tint = cfg.tint;
    this.view.addChild(this.body);
    // Side-mounted laser cannons — visible only when a laser-family weapon is active
    this.cannonL = new Sprite(SPRITES.laserCannon);
    this.cannonL.anchor.set(0.5, 0.5);
    this.cannonL.position.set(8, -14);
    this.cannonL.visible = false;
    this.view.addChild(this.cannonL);
    this.cannonR = new Sprite(SPRITES.laserCannon);
    this.cannonR.anchor.set(0.5, 0.5);
    this.cannonR.position.set(8, 14);
    this.cannonR.visible = false;
    this.view.addChild(this.cannonR);
    // Cockpit dome — sits above ship body
    this.cockpitSpr = new Sprite(SPRITES.cockpitGlow);
    this.cockpitSpr.anchor.set(0.5, 0.5);
    this.cockpitSpr.position.set(2, 0);
    this.cockpitSpr.tint = cfg.tint ?? 0x00ffff;
    this.cockpitSpr.blendMode = 'add';
    this.cockpitSpr.alpha = 0.85;
    this.view.addChild(this.cockpitSpr);
    const glowCol = cfg.tint ?? 0x00ffff;
    try {
      this.glow = new GlowFilter({ distance: 16, outerStrength: 1.8, innerStrength: 0.4, color: glowCol, quality: 0.5 });
      this.view.filters = [this.glow as any];
    } catch {}
    stage.addChild(this.view);
  }

  private drawFlame(boostActive: boolean): void {
    const g = this.flameGfx;
    g.clear();
    this.flameT += 1;
    const flick = 0.7 + 0.3 * Math.sin(this.flameT * 0.7);
    const lenBase = boostActive ? 36 : 14;
    const len = lenBase * flick;
    const baseY = [-7, 0, 7];
    for (const dy of baseY) {
      g.poly([-30, dy - 2.6, -30 - len, dy, -30, dy + 2.6]);
      g.fill({ color: boostActive ? 0xff8800 : 0xffaa00, alpha: 0.55 });
      // Inner core
      g.poly([-30, dy - 1.5, -30 - len * 0.7, dy, -30, dy + 1.5]);
      g.fill({ color: 0xffffaa, alpha: 0.85 });
    }
    // Engine halo: amped pulse on boost, soft idle otherwise
    const idle = 0.85 + 0.10 * Math.sin(this.flameT * 0.18);
    const boostPulse = 1.20 + 0.20 * Math.sin(this.flameT * 0.7);
    const s = boostActive ? boostPulse : idle * 0.65;
    this.engineSpr.scale.set(s, s);
    this.engineSpr.alpha = boostActive
      ? 0.85 + 0.15 * Math.sin(this.flameT * 0.5)
      : 0.35 + 0.05 * Math.sin(this.flameT * 0.18);
  }

  update(dt: number): void {
    if (this.dead) { this.view.visible = false; return; }
    if (this.fireTimer > 0) this.fireTimer -= dt;
    // Respawn arrival — ease the ship in from the left, ignore mouse, stay invuln
    if (this.arrivalT > 0) {
      this.arrivalT = Math.max(0, this.arrivalT - dt);
      const f = 1 - this.arrivalT / this.arrivalDur;
      const eased = 1 - Math.pow(1 - f, 3); // ease-out cubic
      this.x = this.arrivalStartX + (this.arrivalEndX - this.arrivalStartX) * eased;
      this.y = this.arrivalEndY;
      this.vx = 0; this.vy = 0;
      this.aimAng = 0; // facing right during arrival
      this.view.x = this.x;
      this.view.y = this.y;
      this.view.rotation = this.aimAng;
      // Blink while invuln
      if (this.invuln > 0) this.invuln -= 1;
      const visible = !(this.invuln > 0 && Math.floor(this.invuln / 4) % 2 === 1);
      this.view.alpha = visible ? 1 : 0;
      // Trail still draws so the arrival feels propulsive
      this.trail.unshift({ x: this.x, y: this.y });
      if (this.trail.length > TRAIL_MAX) this.trail.pop();
      this.drawFlame(false);
      this.glowT += dt;
      if (this.glow) this.glow.outerStrength = this.baseGlowStrength * (0.85 + 0.15 * Math.sin(this.glowT * 2.5));
      this.drawTrail();
      return;
    }
    const target = G.mouseHistory[0] ?? G.mouse;
    const tx = target.x;
    const ty = target.y;

    const oldest = G.mouseHistory[0] ?? G.mouse;
    const mouseMoving = isTouchDevice || Math.hypot(G.mouse.x - oldest.x, G.mouse.y - oldest.y) > 10;
    const boostActive = keys.boost && this.shield > 0 && mouseMoving;
    const cap = boostActive ? 1400 : MAX_SPEED;

    if (boostActive) {
      // Boost = forward burst along the aim direction, ignoring the mouse target.
      // Velocity snaps to a fixed forward vector (1400 px/s) so shift always feels like a thrust.
      const BOOST_SPEED = 1400;
      this.vx = Math.cos(this.aimAng) * BOOST_SPEED;
      this.vy = Math.sin(this.aimAng) * BOOST_SPEED;
      this.shield = Math.max(0, this.shield - 0.05 * dt * 60);
    } else if (this.warpMomentumT > 0) {
      // Just exited a warp — slingshot momentum, very light drag so speed decays
      // from 2× entry back toward normal over the 1s window.
      this.warpMomentumT = Math.max(0, this.warpMomentumT - dt);
      const drag = Math.pow(0.994, dt * 60);
      this.vx *= drag;
      this.vy *= drag;
    } else if (this.cfg.useKeys) {
      if (keys.up)    this.vy -= KEYS_ACCEL * dt;
      if (keys.down)  this.vy += KEYS_ACCEL * dt;
      if (keys.left)  this.vx -= KEYS_ACCEL * dt;
      if (keys.right) this.vx += KEYS_ACCEL * dt;
      if (!keys.up && !keys.down)   this.vy *= Math.pow(0.08, dt);
      if (!keys.left && !keys.right) this.vx *= Math.pow(0.08, dt);
    } else if (isTouchDevice) {
      const MOB_ACCEL = 1800;
      this.vx += keys.joyX * MOB_ACCEL * dt;
      this.vy += keys.joyY * MOB_ACCEL * dt;
      if (Math.abs(keys.joyX) < 0.05) this.vx *= Math.pow(0.08, dt);
      if (Math.abs(keys.joyY) < 0.05) this.vy *= Math.pow(0.08, dt);
    } else {
      const k = VEL_LERP * dt;
      this.vx += ((tx - this.x) * TARGET_PULL - this.vx) * k;
      this.vy += ((ty - this.y) * TARGET_PULL - this.vy) * k;
    }

    if (keys.brake) {
      const decay = Math.pow(0.85, dt * 60);
      this.vx *= decay;
      this.vy *= decay;
    }

    const sp = Math.hypot(this.vx, this.vy);
    if (sp > cap) {
      this.vx = (this.vx / sp) * cap;
      this.vy = (this.vy / sp) * cap;
    }

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    this.trail.unshift({ x: this.x, y: this.y });
    if (this.trail.length > TRAIL_MAX) this.trail.pop();

    if (this.x < this.radius) { this.x = this.radius; this.vx *= -0.25; }
    if (this.x > G.W - this.radius) { this.x = G.W - this.radius; this.vx *= -0.25; }
    if (this.y < this.radius) { this.y = this.radius; this.vy *= -0.25; }
    if (this.y > G.H - this.radius) { this.y = G.H - this.radius; this.vy *= -0.25; }

    if (this.cfg.useKeys) {
      const sp2 = Math.hypot(this.vx, this.vy);
      if (sp2 > 30) this.aimAng = lerpAng(this.aimAng, Math.atan2(this.vy, this.vx), 0.22);
    } else {
      const dxM = G.mouse.x - this.x;
      const dyM = G.mouse.y - this.y;
      // During boost, skip aimAng update when mouse is very close — avoids atan2 instability causing spin
      if (!boostActive || Math.hypot(dxM, dyM) > 60) {
        this.aimAng = lerpAng(this.aimAng, Math.atan2(dyM, dxM), 0.26);
      }
    }

    this.view.x = this.x;
    this.view.y = this.y;
    this.view.rotation = this.aimAng;

    if (this.invuln > 0) this.invuln -= 1;
    const visible = !(this.invuln > 0 && Math.floor(this.invuln / 4) % 2 === 1);
    this.view.alpha = visible ? 1 : 0;

    // Engine flame pulse — bigger and brighter during boost
    this.drawFlame(keys.boost && this.shield > 0);

    // Hull glow pulse — gentle ±15% breathe
    this.glowT += dt;
    if (this.glow) {
      this.glow.outerStrength = this.baseGlowStrength * (0.85 + 0.15 * Math.sin(this.glowT * 2.5));
    }

    this.drawTrail();
  }

  private buildRopes(stage: Container): void {
    const skin = getActiveTrail();
    const tex = getTrailTextures(skin.id);
    this.currentSkinId = skin.id;
    this.ropeOuter = new MeshRope({ texture: tex.outer, points: this.ropePoints });
    this.ropeMid   = new MeshRope({ texture: tex.mid,   points: this.ropePoints });
    this.ropeCore  = new MeshRope({ texture: tex.core,  points: this.ropePoints });
    this.ropeOuter.tint = skin.outer;
    this.ropeMid.tint   = skin.mid;
    this.ropeCore.tint  = skin.core;
    this.ropeOuter.alpha = 0.55;
    this.ropeMid.alpha   = 0.85;
    this.ropeCore.alpha  = 1;
    for (const r of [this.ropeOuter, this.ropeMid, this.ropeCore]) {
      r.blendMode = 'add';
      stage.addChild(r);
    }
  }

  private syncRopeSkin(): void {
    const skin = getActiveTrail();
    if (skin.id === this.currentSkinId) return;
    const tex = getTrailTextures(skin.id);
    this.ropeOuter.texture = tex.outer;
    this.ropeMid.texture   = tex.mid;
    this.ropeCore.texture  = tex.core;
    this.ropeOuter.tint = skin.outer;
    this.ropeMid.tint   = skin.mid;
    this.ropeCore.tint  = skin.core;
    this.currentSkinId = skin.id;
  }

  private drawTrail(): void {
    this.syncRopeSkin();
    const n = this.trail.length;
    const pts = this.ropePoints;
    const visible = n >= 2;
    this.ropeOuter.visible = this.ropeMid.visible = this.ropeCore.visible = visible;
    if (!visible) { this.trailGfxOverlay.clear(); return; }
    // Head=trail[0], tail=trail[n-1]; pad remaining slots by collapsing to tail.
    const tail = this.trail[n - 1];
    for (let i = 0; i < TRAIL_MAX; i++) {
      const src = i < n ? this.trail[i] : tail;
      pts[i].x = src.x;
      pts[i].y = src.y;
    }
    this.drawTrailOverlay(n);
  }

  private drawTrailOverlay(n: number): void {
    const g = this.trailGfxOverlay;
    g.clear();
    if (n < 3) return;
    // 3-pass HTML-style trail: outer glow → mid flame → white core
    // Pass 1 — wide orange glow (back half)
    for (let i = 1; i < n; i++) {
      const t = i / n;
      const prev = this.trail[i - 1], cur = this.trail[i];
      g.moveTo(prev.x, prev.y);
      g.lineTo(cur.x, cur.y);
      g.stroke({ width: 2 + t * 14, color: 0xff6600, alpha: t * 0.10 });
    }
    // Pass 2 — mid flame (orange→yellow gradient by position)
    for (let i = 1; i < n; i++) {
      const t = i / n;
      const col = t < 0.45 ? 0xff4400 : t < 0.72 ? 0xff9900 : 0xffdd44;
      const prev = this.trail[i - 1], cur = this.trail[i];
      g.moveTo(prev.x, prev.y);
      g.lineTo(cur.x, cur.y);
      g.stroke({ width: 1 + t * 7, color: col, alpha: t * 0.45 });
    }
    // Pass 3 — bright white core (front 40% only)
    const coreStart = Math.floor(n * 0.55);
    for (let i = coreStart + 1; i < n; i++) {
      const t = (i - coreStart) / (n - coreStart);
      const prev = this.trail[i - 1], cur = this.trail[i];
      g.moveTo(prev.x, prev.y);
      g.lineTo(cur.x, cur.y);
      g.stroke({ width: t * 3.5, color: 0xffffff, alpha: t * 0.75 });
    }
  }

  // Tier-escalating glow + ship sprite — color, intensity, AND silhouette evolve with weapon level
  setWeaponTier(weaponIdx: number): void {
    // 11-tier weapon ladder — glow color escalates through 11 distinct shades.
    // Ship sprite array only has 6 entries, so tier 6-10 reuse tier-5 silhouette
    // but with cosmic-color glow (the player visually feels they're past the cap).
    const tier = Math.min(10, Math.floor(weaponIdx / 9));
    const colors = [
      0x00ffff, 0x44ddff, 0xff44ff, 0xffaa00, 0xff2200, 0xffffaa,
      0xcc88ff, 0xff66ff, 0x88ffaa, 0xffcc44, 0xffffff,
    ];
    const outer  = [1.8, 2.0, 2.4, 2.8, 3.2, 3.8, 4.2, 4.6, 5.0, 5.5, 6.0];
    this.baseGlowStrength = outer[tier];
    if (this.glow) {
      this.glow.color = colors[tier];
      this.glow.outerStrength = outer[tier];
    }
    this.cockpitSpr.tint = colors[tier];
    this.engineSpr.tint = colors[tier];
    // Swap to tier-specific ship texture (canards, weapon pods, extra engines, halo at tier 5)
    // Sprite atlas now has 11 silhouettes (0..10) — full tier mapping
    const tex = SPRITES.playerP1Tiers[tier];
    if (tex && this.body.texture !== tex) this.body.texture = tex;
    // Side cannons retint with tier color too
    this.cannonL.tint = colors[tier];
    this.cannonR.tint = colors[tier];
  }

  // Show/hide side laser cannons based on the active weapon's name (substring match).
  setWeaponName(name: string): void {
    const n = (name || '').toUpperCase();
    const isLaser = n.indexOf('LASER') >= 0 || n.indexOf('RAYON') >= 0;
    this.cannonL.visible = isLaser;
    this.cannonR.visible = isLaser;
  }

  // Hide/show the ship + its trail ropes — used during respawn-delay window
  setVisible(visible: boolean): void {
    this.view.visible = visible;
    this.ropeOuter.visible = visible;
    this.ropeMid.visible = visible;
    this.ropeCore.visible = visible;
    this.trailGfxOverlay.visible = visible;
  }

  reset(x: number, y: number, instant = false): void {
    if (instant) {
      // First-spawn / fresh-game path — snap into place, controllable immediately, short invuln
      this.arrivalT = 0;
      this.x = x; this.y = y;
      this.vx = 0; this.vy = 0;
      this.hp = this.maxHp; this.shield = this.maxShield;
      this.invuln = 60; // ~1s of grace blink
      this.trail.length = 0;
      this.view.x = this.x;
      this.view.y = this.y;
      this.view.alpha = 1;
      return;
    }
    // Death-respawn path — slide-in arrival from the left with extended blink invuln
    this.arrivalStartX = -60;
    this.arrivalEndX = x;
    this.arrivalEndY = y;
    this.arrivalDur = 1.4;
    this.arrivalT = 1.4;
    this.x = this.arrivalStartX; this.y = y;
    this.vx = 0; this.vy = 0;
    this.hp = this.maxHp; this.shield = this.maxShield;
    this.invuln = 240;
    this.trail.length = 0;
  }
}

function lerpAng(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

export function pushMouseHistory(): void {
  G.mouseHistory.push({ x: G.mouse.x, y: G.mouse.y });
  if (G.mouseHistory.length > MOUSE_LAG_FRAMES) G.mouseHistory.shift();
}
